<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import UiButton from '../UiButton.vue'
import UiChoiceCard from '../UiChoiceCard.vue'
import UiPill from '../UiPill.vue'
import UiTextField from '../UiTextField.vue'
import { QR_CODE_PROVIDERS, type WhatsAppConnectionView, type WhatsAppConnectionInput } from './types'

// Connects a WhatsApp number through a provider (whatsapp-api): the official Meta Cloud API,
// Z-API, Wafly or WPPConnect. Used by the portal (the tenant's dedicated number) and by the platform console (Aubaine's
// shared number). The app does the API calls; this only collects and shows. Secrets are never
// shown back: empty fields keep the stored ones.
const props = defineProps<{
  connection: WhatsAppConnectionView | null
  saving?: boolean
  testing?: boolean
  removing?: boolean
  // Z-API / Wafly / WPPConnect: data URL of the QR code while it is shown (null = hidden).
  qrImage?: string | null
  qrLoading?: boolean
  errors?: Record<string, string | undefined>
}>()

const emit = defineEmits<{
  save: [input: WhatsAppConnectionInput]
  test: []
  remove: []
  showQr: []
  hideQr: []
  copy: [value: string]
}>()

type Provider = WhatsAppConnectionInput['provider']
const PROVIDERS: readonly Provider[] = ['meta_cloud', 'z_api', 'wafly', 'wpp_connect']

const provider = ref<Provider>('meta_cloud')
const meta = reactive({ phoneNumberId: '', businessAccountId: '', accessToken: '', appSecret: '' })
const zapi = reactive({ instanceId: '', instanceToken: '', clientToken: '' })
const wafly = reactive({ instanceId: '', instanceToken: '', clientToken: '' })
const wppConnect = reactive({ sessionName: '' })

function load() {
  const current = props.connection
  provider.value = PROVIDERS.find((value) => value === current?.provider) ?? 'meta_cloud'
  meta.phoneNumberId = current?.meta?.phoneNumberId ?? ''
  meta.businessAccountId = current?.meta?.businessAccountId ?? ''
  meta.accessToken = ''
  meta.appSecret = ''
  zapi.instanceId = current?.zapi?.instanceId ?? ''
  zapi.instanceToken = ''
  zapi.clientToken = ''
  wafly.instanceId = current?.wafly?.instanceId ?? ''
  wafly.instanceToken = ''
  wafly.clientToken = ''
  wppConnect.sessionName = current?.wppConnect?.sessionName ?? ''
}
watch(() => props.connection, load, { immediate: true })

const configured = computed(() => !!props.connection?.provider && props.connection.status !== 'not_configured')
const sameProvider = computed(() => props.connection?.provider === provider.value)
const keepHint = (hint: string | null | undefined) =>
  hint && sameProvider.value ? `Atual termina em ${hint}. Deixe em branco para manter.` : undefined

const STATUS: Record<string, { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  connected: { label: 'Conectado', tone: 'success' },
  pending: { label: 'Aguardando conexão', tone: 'warning' },
  failed: { label: 'Falhou', tone: 'danger' },
  disconnected: { label: 'Desconectado', tone: 'warning' },
  not_configured: { label: 'Não conectado', tone: 'neutral' },
}
const showQrBox = computed(
  () =>
    QR_CODE_PROVIDERS.includes(props.connection?.provider ?? '') &&
    (props.connection?.status === 'pending' || props.connection?.status === 'disconnected'),
)

const status = computed(() => STATUS[props.connection?.status ?? 'not_configured'] ?? STATUS.not_configured!)

const phone = computed(() => {
  const digits = (props.connection?.phoneNumber ?? '').replace(/\D/g, '')
  return digits.length >= 12 ? `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, -4)}-${digits.slice(-4)}` : digits || null
})
const checkedAt = computed(() =>
  props.connection?.lastCheckedAtUtc
    ? new Date(props.connection.lastCheckedAtUtc).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    : null,
)

function submit() {
  const blank = (value: string) => value.trim() || null
  const inputs: Record<Provider, () => WhatsAppConnectionInput> = {
    meta_cloud: () => ({
      provider: 'meta_cloud',
      meta: {
        phoneNumberId: blank(meta.phoneNumberId),
        businessAccountId: blank(meta.businessAccountId),
        accessToken: blank(meta.accessToken),
        appSecret: blank(meta.appSecret),
      },
    }),
    z_api: () => ({
      provider: 'z_api',
      zapi: { instanceId: blank(zapi.instanceId), instanceToken: blank(zapi.instanceToken), clientToken: blank(zapi.clientToken) },
    }),
    wafly: () => ({
      provider: 'wafly',
      wafly: { instanceId: blank(wafly.instanceId), instanceToken: blank(wafly.instanceToken), clientToken: blank(wafly.clientToken) },
    }),
    wpp_connect: () => ({ provider: 'wpp_connect', wppConnect: { sessionName: blank(wppConnect.sessionName) } }),
  }
  emit('save', inputs[provider.value]())
}
</script>

<template>
  <div class="wa">
    <div class="wa__status">
      <UiPill :tone="status.tone">{{ status.label }}</UiPill>
      <span v-if="phone" class="wa__phone">{{ phone }}</span>
      <span v-if="connection?.displayName" class="wa__muted">{{ connection.displayName }}</span>
      <span v-if="checkedAt" class="wa__muted">Última verificação: {{ checkedAt }}</span>
    </div>
    <p v-if="connection?.lastError && connection.status !== 'connected'" role="alert" class="wa__error">{{ connection.lastError }}</p>

    <div role="radiogroup" aria-label="Provedor do WhatsApp" class="wa__providers">
      <UiChoiceCard
        kind="radio"
        :selected="provider === 'meta_cloud'"
        title="WhatsApp oficial (Meta)"
        description="API oficial da Meta (WhatsApp Cloud API). Exige uma conta do WhatsApp Business verificada no Gerenciador do WhatsApp."
        @select="provider = 'meta_cloud'"
      />
      <UiChoiceCard
        kind="radio"
        :selected="provider === 'z_api'"
        title="Z-API"
        description="Conecta o número lendo um QR code no celular. Não é oficial: o WhatsApp pode bloquear números com uso fora das regras."
        @select="provider = 'z_api'"
      />
      <UiChoiceCard
        kind="radio"
        :selected="provider === 'wafly'"
        title="Wafly"
        description="Também conecta o número por QR code, sem cobrança por mensagem. Não é oficial: vale o mesmo risco de bloqueio da Z-API."
        @select="provider = 'wafly'"
      />
      <UiChoiceCard
        kind="radio"
        :selected="provider === 'wpp_connect'"
        title="WPPConnect"
        description="Conecta o número por QR code num servidor nosso, sem cobrança por mensagem. Não é oficial: o WhatsApp pode banir o número, então use um chip que você aceite perder."
        @select="provider = 'wpp_connect'"
      />
    </div>

    <div v-if="provider === 'meta_cloud'" class="wa__fields">
      <UiTextField v-model="meta.phoneNumberId" label="ID do número de telefone" hint="Phone number ID, em Configuração da API no painel do app da Meta." :error="errors?.['meta.phoneNumberId']" />
      <UiTextField v-model="meta.businessAccountId" label="ID da conta Business (opcional)" hint="Identificação da conta do WhatsApp Business (WhatsApp Business Account ID)." :error="errors?.['meta.businessAccountId']" />
      <UiTextField
        v-model="meta.accessToken"
        type="password"
        label="Token de acesso permanente"
        :hint="keepHint(connection?.meta?.accessTokenHint) ?? 'Gerado para um usuário do sistema no Gerenciador de Negócios, com permissão whatsapp_business_messaging.'"
        :error="errors?.['meta.accessToken']"
        autocomplete="off"
      />
      <UiTextField
        v-model="meta.appSecret"
        type="password"
        label="Chave secreta do app"
        :hint="keepHint(connection?.meta?.appSecretHint) ?? 'Usada para confirmar que as mensagens recebidas vêm da Meta.'"
        :error="errors?.['meta.appSecret']"
        autocomplete="off"
      />
    </div>
    <div v-else-if="provider === 'wpp_connect'" class="wa__fields">
      <p class="wa__error" role="note">
        Uso não oficial: o WPPConnect liga o WhatsApp Web ao servidor. O WhatsApp pode banir o número a qualquer momento e a conversa
        deixa de funcionar até um novo QR code. Não use o número principal da empresa.
      </p>
      <UiTextField
        v-model="wppConnect.sessionName"
        label="Nome da sessão"
        hint="De 3 a 40 letras minúsculas, números e hífen (ex.: acme-vendas). Depois de salvar, leia o QR code no celular."
        :error="errors?.['wppConnect.sessionName']"
        autocomplete="off"
      />
    </div>
    <div v-else-if="provider === 'wafly'" class="wa__fields">
      <UiTextField v-model="wafly.instanceId" label="ID da instância" hint="No painel da Wafly, na página da instância." :error="errors?.['wafly.instanceId']" />
      <UiTextField
        v-model="wafly.instanceToken"
        type="password"
        label="Token da instância"
        :hint="keepHint(connection?.wafly?.instanceTokenHint) ?? 'Gerado junto com a instância, no painel da Wafly.'"
        :error="errors?.['wafly.instanceToken']"
        autocomplete="off"
      />
      <UiTextField
        v-model="wafly.clientToken"
        type="password"
        label="Token de segurança da conta (Client-Token)"
        :hint="keepHint(connection?.wafly?.clientTokenHint) ?? 'Client-Token, em Segurança no painel da Wafly. Obrigatório.'"
        :error="errors?.['wafly.clientToken']"
        autocomplete="off"
      />
    </div>
    <div v-else class="wa__fields">
      <UiTextField v-model="zapi.instanceId" label="ID da instância" hint="No painel da Z-API, em Instâncias." :error="errors?.['zapi.instanceId']" />
      <UiTextField
        v-model="zapi.instanceToken"
        type="password"
        label="Token da instância"
        :hint="keepHint(connection?.zapi?.instanceTokenHint)"
        :error="errors?.['zapi.instanceToken']"
        autocomplete="off"
      />
      <UiTextField
        v-model="zapi.clientToken"
        type="password"
        label="Token de segurança da conta (opcional)"
        :hint="keepHint(connection?.zapi?.clientTokenHint) ?? 'Client-Token, em Segurança no painel da Z-API.'"
        :error="errors?.['zapi.clientToken']"
        autocomplete="off"
      />
    </div>

    <div class="wa__actions">
      <UiButton :loading="saving" @click="submit">{{ configured ? 'Salvar e testar' : 'Conectar número' }}</UiButton>
      <UiButton v-if="configured" variant="gray" :loading="testing" @click="emit('test')">Testar conexão</UiButton>
      <UiButton v-if="configured" variant="plain" tone="destructive" :loading="removing" @click="emit('remove')">Desconectar</UiButton>
    </div>

    <section v-if="showQrBox" class="wa__box">
      <h3 class="wa__box-title">Ler o QR code no celular</h3>
      <p class="wa__muted">No celular do número, abra o WhatsApp › Aparelhos conectados › Conectar um aparelho e aponte a câmera para o código.</p>
      <img v-if="qrImage" :src="qrImage" alt="QR code para conectar o WhatsApp" class="wa__qr" />
      <div class="wa__actions">
        <UiButton v-if="!qrImage" variant="gray" :loading="qrLoading" @click="emit('showQr')">Mostrar QR code</UiButton>
        <UiButton v-else variant="plain" @click="emit('hideQr')">Ocultar QR code</UiButton>
      </div>
    </section>

    <section v-if="configured && connection?.provider === 'meta_cloud'" class="wa__box">
      <h3 class="wa__box-title">Receber mensagens</h3>
      <template v-if="connection.webhookUrl">
        <p class="wa__muted">No painel do app da Meta, em WhatsApp › Configuração › Webhook, use estes dados e assine o campo "messages".</p>
        <div class="wa__copy">
          <span class="wa__label">URL de retorno</span>
          <code class="wa__code">{{ connection.webhookUrl }}</code>
          <UiButton size="small" variant="gray" @click="emit('copy', connection.webhookUrl)">Copiar</UiButton>
        </div>
        <div v-if="connection.webhookVerifyToken" class="wa__copy">
          <span class="wa__label">Token de verificação</span>
          <code class="wa__code">{{ connection.webhookVerifyToken }}</code>
          <UiButton size="small" variant="gray" @click="emit('copy', connection.webhookVerifyToken)">Copiar</UiButton>
        </div>
      </template>
      <p v-else class="wa__muted">O endereço para a Meta entregar as mensagens aparece aqui quando o ambiente tiver um endereço público.</p>
    </section>
  </div>
</template>

<style scoped>
.wa {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.wa__status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.wa__phone {
  font-weight: var(--weight-semibold);
}
.wa__muted {
  margin: 0;
  font-size: var(--type-footnote-size);
  color: var(--text-secondary);
}
.wa__error {
  margin: 0;
  font-size: var(--type-footnote-size);
  color: var(--error);
}
.wa__providers {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 10px;
}
.wa__fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
}
.wa__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.wa__box {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border: var(--hairline) solid var(--separator);
  border-radius: var(--radius-list);
  background: var(--surface-subtle);
}
.wa__box-title {
  margin: 0;
  font-size: var(--type-overline-size);
  letter-spacing: var(--type-overline-tracking);
  text-transform: uppercase;
  color: var(--tertiary-label);
  font-weight: var(--weight-semibold);
}
.wa__qr {
  width: 220px;
  height: 220px;
  border-radius: 8px;
  background: #fff;
  padding: 8px;
}
.wa__copy {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.wa__label {
  min-width: 150px;
  font-size: var(--type-footnote-size);
  font-weight: var(--weight-medium);
  color: var(--text-label);
}
.wa__code {
  padding: 4px 8px;
  border: var(--hairline) solid var(--separator);
  border-radius: var(--radius-md);
  background: var(--surface-card);
  font-size: var(--type-footnote-size);
  word-break: break-all;
}
</style>
